/**
 * Handing a destination to the device's own map app.
 *
 * A link, not an integration. We are not routing, drawing a line or estimating
 * a drive time — we are getting out of the way of the app the user already
 * trusts, already has signed in, and already has their car's Bluetooth paired
 * with. One line of code for something a rep does a dozen times a day.
 */

export interface DirectionsTarget {
	latitude: number
	longitude: number
	/** Shown as the destination label where the platform supports one. */
	label?: string
}

const isIOS = (ua: string): boolean =>
	/iPad|iPhone|iPod/.test(ua) ||
	// iPadOS 13+ reports itself as a Mac. The touch-point count is the usual
	// tell, and it matters here because an iPad should get Apple Maps.
	(/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1)

/**
 * The URL that opens turn-by-turn directions to a point.
 *
 * Coordinates rather than the address string, always: the address is what the
 * geocoder guessed, and on the sites this app exists for it is routinely thin
 * or absent. The pin is what the user actually chose.
 *
 * `maps.apple.com` on iOS — a universal link, so it opens the Maps app rather
 * than a web page, and it is the one iOS will not fight. `geo:` elsewhere,
 * which Android hands to whichever map app the user has set as default.
 */
export const directionsUrl = (
	{ latitude, longitude, label }: DirectionsTarget,
	userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): string => {
	const coords = `${latitude},${longitude}`

	if (isIOS(userAgent)) {
		const params = new URLSearchParams({ daddr: coords, dirflg: 'd' })
		if (label) params.set('q', label)
		return `https://maps.apple.com/?${params.toString()}`
	}

	// The label goes in the query part of a geo: URI, which is not a standard
	// URLSearchParams shape — `geo:lat,lng?q=lat,lng(Label)` is the documented
	// form, and the coordinates must be repeated inside `q` or the label is
	// treated as a search term and the pin moves.
	const query = label ? `?q=${coords}(${encodeURIComponent(label)})` : ''
	return `geo:${coords}${query}`
}
